import type React from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import type {
  ChatMessage,
  GroupChatMessage,
  NotificationOutput,
} from "@/api-types";

export type { ChatMessage };

// Resilient WebSocket with dynamic token updates
class ResilientWebSocket {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts: number;
  private readonly reconnectDelay: number;
  private listeners: Map<string, (event: MessageEvent) => void> = new Map();
  private isManuallyClosed = false;

  constructor(
    private baseUrl: string,
    private token: string | null = null,
    options?: { maxReconnectAttempts?: number; reconnectDelay?: number },
  ) {
    this.maxReconnectAttempts = options?.maxReconnectAttempts ?? 10;
    this.reconnectDelay = options?.reconnectDelay ?? 1000;
  }

  private get fullUrl() {
    return this.token ? `${this.baseUrl}?token=${this.token}` : this.baseUrl;
  }

  connect(): void {
    if (!this.token) {
      console.warn("WebSocket connection blocked: No token provided.");
      return;
    }

    if (this.socket) {
      console.log(`Closing existing WebSocket connection.`);
      this.close(); // Close existing connection before opening a new one
    }

    console.log(`Connecting to WebSocket: ${this.fullUrl}`);
    this.socket = new WebSocket(this.fullUrl);

    this.socket.onopen = () => {
      console.log("WebSocket connected.");
      this.reconnectAttempts = 0;
    };

    this.socket.onmessage = (event: MessageEvent) => {
      this.listeners.forEach((callback) => callback(event));
    };

    this.socket.onclose = (event: CloseEvent) => {
      if (this.isManuallyClosed) return;
      console.warn(
        `WebSocket closed. Code: ${event.code}, Reason: ${event.reason}`,
      );
      this.reconnect();
    };

    this.socket.onerror = (event: Event) => {
      console.error("WebSocket encountered an error:", event);
      this.socket?.close();
    };
  }

  private reconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * 2 ** this.reconnectAttempts,
      30000,
    );
    console.log(`Reconnecting in ${delay / 1000} seconds...`);
    setTimeout(() => this.connect(), delay);
  }

  send(data: string): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(data);
    } else {
      console.warn("Cannot send message: WebSocket is not open.");
    }
  }

  addEventListener(
    eventType: string,
    callback: (event: MessageEvent) => void,
  ): void {
    this.listeners.set(eventType, callback);
  }

  close(): void {
    console.log(`Closing WebSocket connection.`);
    this.isManuallyClosed = true;
    this.socket?.close();
  }

  setToken(newToken: string | null): void {
    if (newToken === null) {
      this.close();
      return;
    }
    if (this.token !== newToken) {
      console.log(`Updating WebSocket token.`);
      this.token = newToken;
      this.connect(); // Reconnect with the new token
    }
  }
}

// Define WebSocket context
interface WebSocketContextType {
  sendMessage: (message: string) => void;
  lastMessage: WsUnspecifiedMessage | null;
  setAuthToken: (token: string | null) => void;
}

// Create the context with a default value
const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined,
);

export type WsUnspecifiedMessage =
  | WsChatMessage
  | WsGroupChatMessage
  | WsNotificationMessage;

// ChatMessage is now imported directly from @/api-types

export interface WsChatMessage {
  type: "chat_message";
  engagementId: string;
  content: ChatMessage;
}

export interface WsGroupChatMessage {
  type: "group_chat_message";
  groupId: string;
  content: GroupChatMessage;
}

export interface WsNotificationMessage {
  type: "notification";
  notification: NotificationOutput;
}

export const WebSocketProvider: React.FC<{
  baseUrl: string;
  children: React.ReactNode;
}> = ({ baseUrl, children }) => {
  const wsRef = useRef<ResilientWebSocket | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [lastWsMessage, setLastWsMessage] = useState<MessageEvent | null>(null);

  useEffect(() => {
    if (!wsRef.current) {
      wsRef.current = new ResilientWebSocket(baseUrl, token);
    }
    wsRef.current.setToken(token);

    wsRef.current.addEventListener("message", (event) => {
      setLastWsMessage(event);
    });
  }, [token, baseUrl]);

  const sendMessage = (message: string) => {
    wsRef.current?.send(message);
  };

  const lastMessage =
    lastWsMessage && (JSON.parse(lastWsMessage?.data) as WsUnspecifiedMessage);

  return (
    <WebSocketContext.Provider
      value={{ sendMessage, lastMessage, setAuthToken: setToken }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook for easy access to the WebSocket context
export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
